import { ArrowDownward, KeyboardArrowDown, ExpandMore, Settings as SettingsIcon } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	Container,
	Menu,
	MenuItem,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Divider,
} from "@mui/material";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import DehazeIcon from '@mui/icons-material/Dehaze';
import Grid from "@mui/material/Grid";
import Main from "components/Main/Main";
import React, { useState } from "react";
import { useData } from "store/DataProvider";
import { useAuth } from "store/AuthProvider";
import { useLogoutModal } from "store/LogoutModalProvider";
import { EventData, StationData } from "types/auth";
import CachePurgeDialog from "components/CachePurgeDialog/CachePurgeDialog";

const Setting = () => {
	const data = useData();
	const auth = useAuth();
	const { openLogoutModal } = useLogoutModal();
	const userEmail = auth.getUserEmailValue() || auth.getUserNameValue() || "";
	const eventList = data.getEventList();
	const stationList = data.getStationList();
	
	const [purgeCacheOpen, setPurgeCacheOpen] = useState(false);
	
	const [showAdvanced, setShowAdvanced] = useState(false);
	
	const handleHiddenClick = () => {
		setShowAdvanced(!showAdvanced);
	};
	const SelectCard = ({
		title,
		content,
		placeholder,
		options,
		handleSelect,
		selectable = false,
	}: {
		title: string;
		content: string;
		placeholder: string;
		options: Array<{ id: string; value: string }>;
		handleSelect: Function;
		selectable?: boolean;
	}) => {
		const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
		const open = Boolean(anchorEl);
		const handleClick = (event) => {
			if (selectable) {
				setAnchorEl(event.currentTarget);
			}
		};
		const handleClose = () => {
			setAnchorEl(null);
		};
		return (
			<>
				<Card onClick={handleClick}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
						<Typography whiteSpace={"nowrap"}>{title}</Typography>
						<Box
							ml={1.5}
							display={"flex"}
							alignItems={"center"}
							gap={1}>
							<Typography color={content ? "primary" : "text.secondary"}>{content || placeholder}</Typography>
							{selectable ? <KeyboardArrowDown /> : <Box width={0} />}
						</Box>
					</Box>
				</Card>

				<Menu
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "right",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
					MenuListProps={{
						"aria-labelledby": "basic-button",
					}}
					PaperProps={{
						sx: {
							maxHeight: 300,
							minWidth: 0,
							...(anchorEl && {
								width: anchorEl.clientWidth,
								maxWidth: anchorEl.clientWidth,
							}),
						},
					}}>
					{options?.map((option) => (
						<MenuItem
							value={option.id}
							key={option.id}
							sx={{ textWrap: "auto" }}
							onClick={() => {
								handleSelect(option.id);
								handleClose();
							}}>
							{option.value}
						</MenuItem>
					))}
				</Menu>
			</>
		);
	};
	return (
		<Main
			title="設定"
			titleAlign="left"
			titleIcon={<DehazeIcon />}
		>
			<Container sx={{ my: 2 }}>
				<Grid
					container
					spacing={1.5}>
					<Grid size={12}>
						<SelectCard
							title="活動名稱"
							content={data.getEventName()}
							placeholder="請選擇活動"
							options={eventList.map((event: EventData) => ({ id: event.id, value: event.eventName }))}
							handleSelect={(id: string) => {
								data.setEventId(id);
							}}
							selectable={eventList.length > 1}
						/>
					</Grid>
					<Grid size={12}>
						<SelectCard
							title="旗站"
							content={data.getStationName()}
							placeholder="請選擇旗站"
							options={stationList.map((station: StationData) => ({ id: station.id, value: station.stationName }))}
							handleSelect={(id: string) => {
								data.setStationId(id);
							}}
							selectable={stationList.length > 1}
						/>
					</Grid>
					<Grid size={12}>
						<Card>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
								<Typography whiteSpace="nowrap">登入電郵</Typography>
								<Typography
									color={userEmail ? "primary" : "text.secondary"}
									sx={{ ml: 1.5, textAlign: "right", wordBreak: "break-all" }}
								>
									{userEmail || "—"}
								</Typography>
							</Box>
						</Card>
					</Grid>
					
					<Grid size={12}>
						<Button
							variant="contained"
							fullWidth
							onClick={openLogoutModal}
						>
							登出
						</Button>
					</Grid>
					
					<Grid size={12}>
						<Box
							onClick={handleHiddenClick}
							sx={{
								height: 20,
								cursor: "default",
								userSelect: "none",
							}}
						>
							<Typography variant="body2" color="text.secondary">
								顯示進階設定
							</Typography>
						</Box>
					</Grid>
					
					{showAdvanced && (
						<Grid size={12}>
							<Accordion
								sx={{
									backgroundColor: "rgba(255, 152, 0, 0.08)",
									border: "1px solid rgba(255, 152, 0, 0.3)",
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMore />}
									sx={{
										"& .MuiAccordionSummary-content": {
											alignItems: "center",
											gap: 1,
										},
									}}
								>
									<SettingsIcon color="warning" fontSize="small" />
									<Typography variant="subtitle2" color="warning.dark">
										進階設定
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Divider sx={{ mb: 2 }} />
									
									<Typography variant="body2" color="text.secondary" gutterBottom>
										⚠️ 以下操作僅在必要時使用
									</Typography>
									
									<Box mt={2}>
										<Button
											variant="outlined"
											fullWidth
											color="warning"
											onClick={() => setPurgeCacheOpen(true)}
											sx={{
												justifyContent: "flex-start",
												textAlign: "left",
												py: 1.5,
											}}
										>
											<Box>
												<Typography variant="body2" fontWeight={600}>
													重設緩存與重新同步數據
												</Typography>
												<Typography variant="caption" color="text.secondary">
													清除所有本地緩存並強制登出
												</Typography>
											</Box>
										</Button>
									</Box>
									
									<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
										提示：清除緩存後需要重新登錄以加載工作站數據。
									</Typography>
								</AccordionDetails>
							</Accordion>
						</Grid>
					)}
				</Grid>
			</Container>
			
			<CachePurgeDialog
				open={purgeCacheOpen}
				onClose={() => setPurgeCacheOpen(false)}
			/>
		</Main>
	);
};

export default Setting;
